import React, { useState, useRef, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { MessageSquare, MoreVertical, Pencil, Trash2, Reply, Plus } from 'lucide-react';
import type { Comment, Profile, CommentLike, ReactionType } from '../types/database';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { cn } from '../lib/utils';
import { useUrlPreviews } from '../hooks/useUrlPreviews';
import ReactionPicker from './ReactionPicker';

interface CommentsProps {
  postId: string;
  comments: (Comment & { profiles?: Profile })[];
  onCommentAdded: (newComment: Comment & { profiles?: Profile }) => void;
  highlightCommentId?: string | null;
}

export default function Comments({ postId, comments, onCommentAdded, highlightCommentId }: CommentsProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showCommentForm, setShowCommentForm] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [editingComment, setEditingComment] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [menuOpenCommentId, setMenuOpenCommentId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [localComments, setLocalComments] = useState(comments);
  const { renderContent, extractUrls, fetchUrlMetadata } = useUrlPreviews();
  const [commentReactions, setCommentReactions] = useState<Record<string, { count: number; userReactionType: ReactionType | null }>>({});

  // Update local comments when props change
  useEffect(() => {
    setLocalComments(comments);
  }, [comments]);

  // Fetch metadata for URLs in comments
  useEffect(() => {
    localComments.forEach(comment => {
      if (comment.content) {
        const urls = extractUrls(comment.content);
        urls.forEach(url => {
          fetchUrlMetadata(url);
        });
      }
    });
  }, [localComments]);

  // Fetch reactions for all comments
  useEffect(() => {
    const fetchReactions = async () => {
      if (!user) return;

      try {
        const { data: reactions, error: reactionsError } = await supabase
          .from('comment_likes')
          .select('comment_id, user_id, reaction_type')
          .in('comment_id', localComments.map(c => c.id));

        if (reactionsError) throw reactionsError;

        const reactionsMap: Record<string, { count: number; userReactionType: ReactionType | null }> = {};
        localComments.forEach(comment => {
          const commentReactions = reactions?.filter(r => r.comment_id === comment.id) || [];
          const userReaction = commentReactions.find(r => r.user_id === user.id);
          reactionsMap[comment.id] = {
            count: commentReactions.length,
            userReactionType: userReaction?.reaction_type || null
          };
        });

        setCommentReactions(reactionsMap);
      } catch (err) {
        console.error('Error fetching comment reactions:', err);
      }
    };

    fetchReactions();
  }, [localComments, user]);

  const handleCommentReaction = async (commentId: string, reactionType: ReactionType) => {
    if (!user) return;

    try {
      const currentReaction = commentReactions[commentId];

      if (currentReaction?.userReactionType === reactionType) {
        const { error } = await supabase
          .from('comment_likes')
          .delete()
          .eq('comment_id', commentId)
          .eq('user_id', user.id);

        if (error) throw error;

        setCommentReactions(prev => ({
          ...prev,
          [commentId]: {
            count: Math.max((prev[commentId]?.count || 0) - 1, 0),
            userReactionType: null
          }
        }));
      } else if (currentReaction?.userReactionType) {
        const { error } = await supabase
          .from('comment_likes')
          .update({ reaction_type: reactionType })
          .eq('comment_id', commentId)
          .eq('user_id', user.id);

        if (error) throw error;

        setCommentReactions(prev => ({
          ...prev,
          [commentId]: {
            ...prev[commentId],
            userReactionType: reactionType
          }
        }));
      } else {
        const { error } = await supabase
          .from('comment_likes')
          .insert({
            comment_id: commentId,
            user_id: user.id,
            reaction_type: reactionType
          });

        if (error) throw error;

        setCommentReactions(prev => ({
          ...prev,
          [commentId]: {
            count: (prev[commentId]?.count || 0) + 1,
            userReactionType: reactionType
          }
        }));
      }
    } catch (err) {
      console.error('Error toggling comment reaction:', err);
    }
  };

  const handleSubmitComment = async (e: React.FormEvent, parentId: string | null = null) => {
    e.preventDefault();
    const content = parentId ? replyContent : newComment;
    if (!content.trim() || !user) return;

    try {
      setLoading(true);
      setError(null);

      const { data, error: commentError } = await supabase
        .from('comments')
        .insert({
          post_id: postId,
          user_id: user.id,
          content: content.trim(),
          parent_id: parentId
        })
        .select(`
          *,
          profiles (
            id,
            full_name,
            avatar_url
          )
        `)
        .single();

      if (commentError) throw commentError;

      if (parentId) {
        setReplyContent('');
        setReplyingTo(null);
      } else {
        setNewComment('');
        setShowCommentForm(false);
      }
      
      if (data) {
        onCommentAdded(data);
        setCommentReactions(prev => ({
          ...prev,
          [data.id]: { count: 0, userReactionType: null }
        }));
      }
    } catch (err: any) {
      console.error('Error adding comment:', err);
      setError(err.message || 'Failed to add comment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditComment = async (commentId: string) => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);

      const { data, error: updateError } = await supabase
        .from('comments')
        .update({ content: editContent.trim() })
        .eq('id', commentId)
        .eq('user_id', user.id)
        .select()
        .single();

      if (updateError) throw updateError;

      setLocalComments(prevComments =>
        prevComments.map(comment =>
          comment.id === commentId
            ? { ...comment, content: editContent.trim() }
            : comment
        )
      );
      setEditingComment(null);
      setEditContent('');
    } catch (err: any) {
      console.error('Error updating comment:', err);
      setError(err.message || 'Failed to update comment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!user) return;

    const confirmDelete = window.confirm('Are you sure you want to delete this comment?');
    if (!confirmDelete) return;

    try {
      setLoading(true);
      setError(null);

      const { error: deleteError } = await supabase
        .from('comments')
        .delete()
        .eq('id', commentId)
        .eq('user_id', user.id);

      if (deleteError) throw deleteError;

      setLocalComments(prevComments => prevComments.filter(comment => comment.id !== commentId));
      setMenuOpenCommentId(null);
    } catch (err: any) {
      console.error('Error deleting comment:', err);
      setError(err.message || 'Failed to delete comment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileClick = (profile?: Profile | null) => {
    if (profile?.id) {
      navigate(`/user/${profile.id}`);
    }
  };

  const getAvatarUrl = (profile?: Profile | null) => {
    if (!profile) return `https://api.dicebear.com/7.x/initials/svg?seed=anonymous`;

    if (profile.avatar_url) {
      if (profile.avatar_url.startsWith('http')) {
        return profile.avatar_url;
      }
      const { data } = supabase.storage.from('avatars').getPublicUrl(profile.avatar_url);
      return data.publicUrl;
    }

    return `https://api.dicebear.com/7.x/initials/svg?seed=${profile.full_name}`;
  };

  const renderComments = (parentId: string | null = null, depth: number = 0) => {
    const filteredComments = localComments.filter(comment => comment.parent_id === parentId);
    
    if (filteredComments.length === 0 && !showCommentForm && depth === 0) {
      return (
        <div className="space-y-4">
          <button
            onClick={() => setShowCommentForm(true)}
            className="w-full flex items-center justify-center space-x-2 px-4 py-2 text-gray-600 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <Plus className="h-5 w-5" />
            <span>Add a comment</span>
          </button>
        </div>
      );
    }

    if (filteredComments.length === 0) return null;

    return (
      <div className={cn("space-y-4", depth > 0 && "ml-8 border-l border-gray-200 pl-4")}>
        {filteredComments.map(comment => {
          const isEditing = editingComment === comment.id;
          const isMenuOpen = menuOpenCommentId === comment.id;
          const isOwnComment = user && comment.user_id === user.id;
          const isHighlighted = comment.id === highlightCommentId;
          const reactionInfo = commentReactions[comment.id] || { count: 0, userReactionType: null };

          return (
            <div 
              key={comment.id} 
              className={cn(
                "space-y-2 transition-colors duration-500",
                isHighlighted && "bg-blue-50 p-2 rounded-lg"
              )}
              id={`comment-${comment.id}`}
              ref={el => {
                if (el && isHighlighted) {
                  setTimeout(() => {
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }, 100);
                }
              }}
            >
              <div className="flex items-start space-x-3">
                <img
                  src={getAvatarUrl(comment.profiles)}
                  alt={comment.profiles?.full_name || 'Anonymous'}
                  className={cn(
                    "h-8 w-8 rounded-full object-cover bg-gray-100",
                    comment.profiles?.id && "cursor-pointer hover:opacity-80"
                  )}
                  onClick={() => handleProfileClick(comment.profiles)}
                  onError={(e) => {
                    const img = e.target as HTMLImageElement;
                    img.src = `https://api.dicebear.com/7.x/initials/svg?seed=${comment.profiles?.full_name || 'anonymous'}`;
                  }}
                />
                <div className="flex-1">
                  <div className="bg-gray-50 rounded-lg px-4 py-2">
                    <div className="flex items-center justify-between">
                      <div 
                        className={cn(
                          "font-medium text-sm text-gray-900",
                          comment.profiles?.id && "cursor-pointer hover:text-blue-600"
                        )}
                        onClick={() => handleProfileClick(comment.profiles)}
                      >
                        {comment.profiles?.full_name || 'Anonymous User'}
                      </div>
                      {isOwnComment && (
                        <div className="relative">
                          <button
                            onClick={() => setMenuOpenCommentId(isMenuOpen ? null : comment.id)}
                            className="p-1 rounded-full hover:bg-gray-100 focus:outline-none"
                          >
                            <MoreVertical className="h-4 w-4 text-gray-500" />
                          </button>

                          {isMenuOpen && (
                            <div className="absolute right-0 mt-1 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                              <div className="py-1" role="menu">
                                <button
                                  onClick={() => {
                                    setEditingComment(comment.id);
                                    setEditContent(comment.content);
                                    setMenuOpenCommentId(null);
                                  }}
                                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                >
                                  <Pencil className="h-4 w-4 mr-2" />
                                  Edit
                                </button>
                                <button
                                  onClick={() => handleDeleteComment(comment.id)}
                                  className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    {isEditing ? (
                      <div className="mt-2">
                        <textarea
                          value={editContent}
                          onChange={(e) => setEditContent(e.target.value)}
                          className="w-full p-2 border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          rows={2}
                        />
                        <div className="mt-2 flex justify-end space-x-2">
                          <button
                            onClick={() => setEditingComment(null)}
                            className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded-md"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => handleEditComment(comment.id)}
                            disabled={loading || !editContent.trim()}
                            className="px-3 py-1 text-sm text-white bg-blue-500 rounded-md hover:bg-blue-600 disabled:opacity-50"
                          >
                            Save
                          </button>
                        </div>
                      </div>
                    ) : (
                      renderContent(comment.content)
                    )}
                  </div>
                  <div className="flex items-center space-x-4 mt-1 text-xs">
                    {user && (
                      <>
                        <div className="flex items-center space-x-1">
                          <ReactionPicker
                            currentReaction={reactionInfo.userReactionType}
                            onReactionSelect={(type) => handleCommentReaction(comment.id, type)}
                            compact
                          />
                          {reactionInfo.count > 0 && (
                            <span className="text-gray-500 text-xs ml-1">
                              {reactionInfo.count}
                            </span>
                          )}
                        </div>
                        <button
                          onClick={() => setReplyingTo(replyingTo === comment.id ? null : comment.id)}
                          className="flex items-center space-x-1 text-gray-500 hover:text-blue-500"
                        >
                          <Reply className="h-4 w-4" />
                          <span>Reply</span>
                        </button>
                      </>
                    )}
                  </div>
                  
                  {replyingTo === comment.id && (
                    <form onSubmit={(e) => handleSubmitComment(e, comment.id)} className="mt-2">
                      <div className="flex items-start space-x-2">
                        <textarea
                          value={replyContent}
                          onChange={(e) => setReplyContent(e.target.value)}
                          placeholder="Write a reply..."
                          className="flex-1 p-2 border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          rows={2}
                        />
                        <button
                          type="submit"
                          disabled={loading || !replyContent.trim()}
                          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                        >
                          {loading ? 'Sending...' : 'Reply'}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </div>
              
              {renderComments(comment.id, depth + 1)}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {!showCommentForm && localComments.length > 0 && (
        <button
          onClick={() => setShowCommentForm(true)}
          className="w-full flex items-center justify-center space-x-2 px-4 py-2 text-gray-600 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <Plus className="h-5 w-5" />
          <span>Add a comment</span>
        </button>
      )}
      
      {showCommentForm && (
        <form onSubmit={(e) => handleSubmitComment(e)} className="space-y-2">
          <textarea
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Write a comment..."
            className="w-full p-3 border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            rows={3}
          />
          
          {error && (
            <p className="text-sm text-red-600">{error}</p>
          )}
          
          <div className="flex justify-end space-x-2">
            <button
              type="button"
              onClick={() => {
                setShowCommentForm(false);
                setNewComment('');
              }}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !newComment.trim()}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
            >
              {loading ? 'Posting...' : 'Post Comment'}
            </button>
          </div>
        </form>
      )}

      {error && (
        <div className="text-red-500 text-sm">{error}</div>
      )}

      {renderComments()}
    </div>
  );
}